import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/authContext";
import api from "@/lib/api";
import OnboardingBackdrop from "./OnboardingBackdrop";
import StepIndicator from "./StepIndicator";
import Step1IdeaVision from "./Step1IdeaVision";
import Step2StageGoal from "./Step2StageGoal";
import Step3Geography from "./Step3Geography";
import Step4Budget from "./Step4Budget";
import Step5Team from "./Step5Team";
import Step6OperatingStyle from "./Step6OperatingStyle";

const TOTAL_STEPS = 6;

const buildInitialDraft = (existing, fallbackName) => ({
  // Legacy bits we don't re-ask
  user_name: existing?.user_name || fallbackName || "",
  company_name:
    existing?.company_name ||
    existing?.company ||
    (fallbackName
      ? `${fallbackName.split(" ")[0]} Company`
      : "Your Company"),
  company_tagline: existing?.company_tagline || "",

  // Step 1
  building: existing?.building || null,
  target_customer: existing?.target_customer || null,
  core_problem: existing?.core_problem || null,
  revenue_model: existing?.revenue_model || null,

  // Step 2
  startup_stage: existing?.startup_stage || null,
  main_goal: existing?.main_goal || null,
  target_timeline: existing?.target_timeline || null,
  success_90d: existing?.success_90d || [],

  // Step 3
  country_based: existing?.country_based || "",
  country_of_incorporation: existing?.country_of_incorporation || "",
  target_market: existing?.target_market || null,

  // Step 4
  budget_available: existing?.budget_available || null,
  funding_source: existing?.funding_source || null,

  // Step 5
  team_size: existing?.team_size || null,
  founder_role: existing?.founder_role || null,
  team_skills: existing?.team_skills || [],
  hours_per_week: existing?.hours_per_week || null,

  // Step 6
  operating_mode: existing?.operating_mode || null,
  aggressiveness: existing?.aggressiveness || null,
  biggest_bottleneck: existing?.biggest_bottleneck || null,
  help_needed: existing?.help_needed || null,
});

export default function OnboardingModal() {
  const onboardingOpen = useStore((s) => s.onboardingOpen);
  const closeOnboarding = useStore((s) => s.closeOnboarding);
  const onboardingThen = useStore((s) => s.onboardingThen);
  const setOnboarding = useStore((s) => s.setOnboarding);
  const hydrate = useStore((s) => s.hydrateOnboardingFromServer);
  const existing = useStore((s) => s.onboarding);
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(() =>
    buildInitialDraft(existing, user?.name),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!onboardingOpen) return;
    setStep(1);
    setDraft(buildInitialDraft(existing, user?.name));
  }, [onboardingOpen, existing, user?.name]);

  const progressLabel = useMemo(
    () => `Step ${step} of ${TOTAL_STEPS}`,
    [step],
  );

  const next = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const save = async ({ skipped = false } = {}) => {
    setSubmitting(true);
    const payload = {
      user_name: draft.user_name || user?.name || "Founder",
      user_email: user?.email || null,
      company_name: draft.company_name || "Untitled Co.",
      company_tagline: draft.company_tagline || "",
      industry: "SaaS", // legacy — server derives when missing; keep a sensible default
      blockers: [],
      building: draft.building,
      target_customer: draft.target_customer,
      core_problem: draft.core_problem,
      revenue_model: draft.revenue_model,
      startup_stage: draft.startup_stage,
      main_goal: draft.main_goal,
      target_timeline: draft.target_timeline,
      success_90d: draft.success_90d || [],
      country_based: draft.country_based || null,
      country_of_incorporation: draft.country_of_incorporation || null,
      target_market: draft.target_market,
      budget_available: draft.budget_available,
      funding_source: draft.funding_source,
      team_size: draft.team_size,
      founder_role: draft.founder_role,
      team_skills: draft.team_skills || [],
      hours_per_week: draft.hours_per_week,
      operating_mode: draft.operating_mode,
      aggressiveness: draft.aggressiveness,
      biggest_bottleneck: draft.biggest_bottleneck,
      help_needed: draft.help_needed,
      skipped,
    };
    try {
      const { data } = await api.put("/api/onboarding", payload);
      hydrate(data);
      setOnboarding(data);
      const then = onboardingThen;
      closeOnboarding();
      if (typeof then === "function") then();
      toast.success(skipped ? "Saved what we have" : "Boardroom ready");
      return true;
    } catch (err) {
      toast.error("Couldn't save your profile", {
        description: err?.response?.data?.detail || err.message,
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    const ok = await save({ skipped: true });
    if (ok) closeOnboarding();
  };

  return (
    <AnimatePresence>
      {onboardingOpen && (
        <OnboardingBackdrop onClose={() => closeOnboarding()}>
          <motion.section
            data-testid="onboarding-modal"
            key="modal"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mx-auto flex h-[100dvh] w-full flex-col overflow-hidden border border-white/10 bg-[#0B0B0E] p-5 text-white shadow-[0_40px_80px_-40px_rgba(0,0,0,0.7)] sm:max-h-[92vh] sm:h-auto sm:max-w-[720px] sm:rounded-[28px] sm:p-7 md:p-9"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/10">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2.4} />
                </span>
                <div>
                  <div className="eyebrow text-white/55" data-testid="onboarding-progress">
                    {progressLabel} · Takes ~3 minutes
                  </div>
                  <div className="text-[12.5px] font-semibold text-white">
                    Boardroom setup
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid="onboarding-close"
                onClick={() => closeOnboarding()}
                className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Close onboarding"
              >
                <X className="h-4 w-4" strokeWidth={2.4} />
              </button>
            </header>

            <StepIndicator step={step - 1} total={TOTAL_STEPS} />

            <div className="idea-scroll mt-4 flex-1 overflow-y-auto pr-2">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <Step1IdeaVision key="s1" draft={draft} setDraft={setDraft} next={next} />
                )}
                {step === 2 && (
                  <Step2StageGoal key="s2" draft={draft} setDraft={setDraft} next={next} prev={prev} />
                )}
                {step === 3 && (
                  <Step3Geography key="s3" draft={draft} setDraft={setDraft} next={next} prev={prev} />
                )}
                {step === 4 && (
                  <Step4Budget key="s4" draft={draft} setDraft={setDraft} next={next} prev={prev} />
                )}
                {step === 5 && (
                  <Step5Team key="s5" draft={draft} setDraft={setDraft} next={next} prev={prev} />
                )}
                {step === 6 && (
                  <Step6OperatingStyle
                    key="s6"
                    draft={draft}
                    setDraft={setDraft}
                    finish={() => save()}
                    prev={prev}
                    submitting={submitting}
                  />
                )}
              </AnimatePresence>
            </div>

            <footer className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
              <button
                type="button"
                data-testid="onboarding-skip"
                onClick={handleSkip}
                className="text-[11.5px] font-semibold text-white/45 hover:text-white/80"
              >
                Skip for now
              </button>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">
                Your answers feed every debate
              </div>
            </footer>
          </motion.section>
        </OnboardingBackdrop>
      )}
    </AnimatePresence>
  );
}
